import { zonedToUtc, zonedParts } from './tz';

const STEP = 5 * 60000;
const toMin = (hhmm) => {
  const [h, m] = String(hhmm || '00:00').slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};

// The night that ended most recently before `nowMs`, shifted back `offset` nights.
export function nightWindow(policy, nowMs, offset = 0) {
  const tz = policy.timezone || 'UTC';
  const start = toMin(policy.night_hours_start);
  const end = toMin(policy.night_hours_end);
  const wraps = start > end; // 22:00–06:00 = after 22:00 OR before 06:00
  const now = zonedParts(nowMs, tz);
  const endsToday = now.minutes >= end;
  const endDay = new Date(Date.UTC(now.y, now.mo, now.d - (endsToday ? 0 : 1) - offset));
  const startDay = new Date(Date.UTC(endDay.getUTCFullYear(), endDay.getUTCMonth(), endDay.getUTCDate() - (wraps ? 1 : 0)));
  const at = (day, min) => zonedToUtc(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), Math.floor(min / 60), min % 60, tz);
  return { fromMs: at(startDay, start), toMs: at(endDay, end), tz };
}

// Longest run of consecutive slots matching `pred`, plus all runs ≥ minSlots.
function runs(slots, pred, minSlots = 1) {
  const out = [];
  let s = null;
  slots.forEach((x, i) => {
    if (pred(x)) { if (s == null) s = i; }
    else if (s != null) { out.push([s, i - 1]); s = null; }
  });
  if (s != null) out.push([s, slots.length - 1]);
  return out.filter(([a, b]) => b - a + 1 >= minSlots);
}

// buckets: 5-min rows (group_key = device uuid) restricted to `deviceIds`.
export function summariseNight(buckets, deviceIds, { fromMs, toMs }, silenceMin = 30) {
  const ids = new Set(deviceIds);
  const map = new Map();
  for (const b of buckets) {
    if (!ids.has(b.group_key)) continue;
    const ts = new Date(b.bucket_start).getTime();
    if (ts < fromMs || ts >= toMs) continue;
    const cur = map.get(ts) || { motion: 0, lastMotion: null };
    cur.motion += b.motion_samples || 0;
    if (b.last_motion_at && (!cur.lastMotion || b.last_motion_at > cur.lastMotion)) cur.lastMotion = b.last_motion_at;
    map.set(ts, cur);
  }
  const slots = [];
  for (let ts = Math.ceil(fromMs / STEP) * STEP; ts < toMs; ts += STEP) slots.push({ ts, ...(map.get(ts) || { gap: true }) });

  const moving = slots.filter((x) => !x.gap && x.motion > 0);
  const span = ([a, b]) => ({ fromMs: slots[a].ts, toMs: slots[b].ts + STEP, minutes: (b - a + 1) * 5 });
  const still = runs(slots, (x) => !x.gap && x.motion === 0).map(span).sort((a, b) => b.minutes - a.minutes)[0] || null;
  const silences = runs(slots, (x) => x.gap, Math.ceil(silenceMin / 5)).map(span);
  const reported = slots.filter((x) => !x.gap).length;
  // "Got up" episodes: movement runs, merging pauses of ≤10 min.
  const episodes = [];
  let lastIdx = -99;
  slots.forEach((x, i) => {
    if (x.gap || !(x.motion > 0)) return;
    if (i - lastIdx > 3) episodes.push({ from: x.ts, to: x.ts + STEP });
    else episodes[episodes.length - 1].to = x.ts + STEP;
    lastIdx = i;
  });

  return {
    episodes,
    activeWindows: moving.length,
    detections: moving.reduce((s, x) => s + x.motion, 0),
    firstMovementMs: moving[0]?.ts ?? null,
    lastMovementIso: moving.length ? moving[moving.length - 1].lastMotion : null,
    longestStill: still,
    silences,
    coverage: slots.length ? reported / slots.length : 0,
  };
}

// Below this the sensor mostly stopped reporting; matches the backend's sensor_silent status.
export const SILENT_COVERAGE = 0.5;

const median = (xs) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// Flag nights against the resident's own median (nights with data only).
export function flagNights(nights) {
  const withData = nights.filter((n) => n.summary.coverage >= SILENT_COVERAGE);
  const medActive = median(withData.map((n) => n.summary.activeWindows));
  const medStill = median(withData.map((n) => n.summary.longestStill?.minutes || 0));
  return nights.map((n) => {
    const s = n.summary;
    if (s.coverage === 0) return { ...n, flag: 'nodata' };
    if (s.coverage < SILENT_COVERAGE) return { ...n, flag: 'silent' };
    if (withData.length < 4) return { ...n, flag: null };
    const still = s.longestStill?.minutes || 0;
    if (medActive > 0 && s.activeWindows >= medActive * 1.5 && s.activeWindows - medActive >= 6) return { ...n, flag: 'restless' };
    if ((medActive > 0 && s.activeWindows <= medActive * 0.5) || (medStill > 0 && still >= medStill * 1.5 && still - medStill >= 60)) {
      return { ...n, flag: 'still' };
    }
    return { ...n, flag: null };
  });
}
